import * as yup from 'yup'
import { useSnackbar } from 'notistack'
import { useContext, useEffect } from 'react'
import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useMeta } from '@contexts/Meta'
import useAPIFetch from '@hooks/useAPIFetch'
import { RoleInfo } from '@models/role.model'
import { dirtyValues } from '@utils/filterDirtyFields'
import Header from '@components/configurations/header'
import UserService from '@services/users/user.service'
import { FormSwitch } from '@components/configurations/form/switch'
import { FormTextField } from '@components/configurations/form/textfield'
import ValidationToast from '@components/configurations/form/ValidationToast'
import { CompactFieldRow } from '@components/configurations/form/compactFieldRow'
import AddingUsersTable from '@components/configurations/users_config/user_groups/form/addingUser'
import { ReactComponent as ProjectAccessSettingIcon } from '@assets/sideMenuIcon/project_access_setting.svg'
import { PermissionSettingTable } from '@components/configurations/users_config/user_groups/form/permissionSettingTable'
import CommonService from '@services/common/common.service'
import { OptionsContext } from '../../../contexts/Options/provider'
import { isEqual } from 'lodash'
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded'
import { AuthContext } from '@contexts/Auth'
import { hasPermission } from '@utils/permission_helper'
import { PermissionCodeEnum } from '@models/permission'

export const EditUserGroups = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { setRequest, isLoading } = useAPIFetch()
	const { state, dispatch } = useContext(OptionsContext)
	const { state: authState } = useContext(AuthContext)

	const validationSchema = yup
		.object()
		.shape({
			code: yup.string().max(255).trim().required('Role Name is required'),
			active: yup.boolean(),
		})
		.required()

	const methods = useForm<RoleInfo>({
		resolver: yupResolver(validationSchema),
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})
	const {
		reset,
		handleSubmit,
		control,
		formState: { isDirty, dirtyFields },
		getValues,
	} = methods

	const permissionSettingItems = useWatch({
		control,
		name: 'project_product_subs',
	})
	const { project_id, role_id } = useParams<string>()

	const onSubmit = (data: RoleInfo) => {
		if (
			!isDirty &&
			isEqual(
				data.role_profile_ids,
				data.role_users?.map((x) => x.profile_id),
			)
		) {
			enqueueSnackbar('You have no update in this session.', { variant: 'warning' })
		} else {
			let new_value: any = dirtyValues(dirtyFields, data)

			//for user list
			let _role_users = data.role_users?.map((x) => {
				return x.profile_id
			})
			let _role_profile_ids = data.role_profile_ids
			let addedUser = _role_profile_ids
				?.filter((old) => _role_users?.indexOf(old) === -1)
				?.map((x) => {
					return { profile_id: x, active: true }
				})
			let deletedUser = _role_users
				?.filter((old) => (old === undefined ? false : _role_profile_ids?.indexOf(old) === -1))
				?.map((x = '') => {
					return { profile_id: x, active: false }
				})

			let new_role_users: { profile_id?: string; active: boolean }[] = []
			if (deletedUser && addedUser) {
				new_role_users = addedUser?.concat(deletedUser)
			}

			//for permission setting
			let thisProjectSubs = data.project_subs

			let new_item_subs: any[] = []
			data.project_product_subs?.forEach((x) => {
				if (
					thisProjectSubs?.map((sub) => sub.item_id)?.includes(x?.item_id ?? '') &&
					!x.isSubscribe
				) {
					new_item_subs.push(x)
				} else if (
					!thisProjectSubs?.map((sub) => sub.item_id)?.includes(x?.item_id ?? '') &&
					x.isSubscribe
				) {
					new_item_subs.push(x)
				}
			})

			const new_project_subs = new_item_subs?.map((x) => {
				return {
					item_type: x.item_type,
					item_id: x.item_id,
					active: x.isSubscribe,
				}
			})

			new_value = {
				...new_value,
				role_users: new_role_users,
				project_subs: new_project_subs,
				active: data.base_system_field?.active,
				permissions: data.permissionsDiff,
			}
			delete new_value.role_profile_ids
			setRequest({
				callback: async (cancelToken: any) => {
					await UserService.UpdateUserGroup(new_value, role_id ?? '').then(() => {
						enqueueSnackbar('User role updated.', { variant: 'success' })
						setMeta({ isLoading: false })

						CommonService.GetCategoryMetadata('role')
							.then((results) => {
								dispatch({ type: 'updateRole', options: results })
							})
							.then(() => {
								navigate(-1)
							})
					})
				},
			})
		}
	}

	const onError = (errors: any) => {
		console.log('errors', errors)
	}

	const reload = async () => {
		setRequest({
			callback: async () => {
				if (project_id && role_id) {
					const projectInfo = await UserService.GetProject(project_id)

					await UserService.GetUserGroup(role_id)
						.then(async (f) => {
							if (f) {
								let thisProjectSubs = f.project_subs

								let allSubscriptions = projectInfo.subscriptions?.map((x) => {
									let isSubscribe =
										thisProjectSubs?.some((item) => {
											return item.item_id === x.item_id
										}) ?? false
									return {
										...x,
										isSubscribe,
									}
								})

								f.project_product_subs = allSubscriptions

								f.role_users = f.role_profile_ids?.map((x) => {
									return {
										profile_id: x,
										active: true,
									}
								})
								f.selectedInDialog = []
								f.originalPermissions = [...(f.permissions || [])]
								console.log('role', f)
								if (f) reset(f)
							}
						})
						.catch((err) => {
							enqueueSnackbar(err.response.data.message, {
								variant: 'error',
							})
							navigate(`/configurations/users_config/user_groups/${project_id}`)
						})
				}
			},
		})
	}

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	useEffect(() => {
		reload()
	}, [])

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ArrowBackIosRoundedIcon
					fontSize={'large'}
					sx={{ cursor: 'pointer', color: '#707070' }}
					onClick={() => {
						navigate(`/configurations/users_config/user_groups/${project_id}`)
					}}
				/>
				<Header
					title={t('Edit User Role')}
					icon={<PersonIcon sx={{ fontSize: 35, color: '#707070' }} />}
				/>
			</Box>

			<Box component="form" onSubmit={handleSubmit(onSubmit, onError)} sx={{ marginX: '45px' }}>
				<FormProvider {...methods}>
					<CompactFieldRow
						firstchildren={
							<FormTextField
								name="display_name"
								control={control}
								label={t('Role Name')}
								mandatory={true}
								uneditable={true}
							/>
						}
						secondchildren={
							<FormSwitch name="base_system_field.active" control={control} label={t('Active')} />
						}
					/>

					<CompactFieldRow
						firstchildren={<FormSwitch name="is_admin" control={control} label={t('Admin')} />}
						secondchildren={<></>}
					/>

					<Box
						sx={{
							margin: '25px 0 0 0',
						}}
					>
						<Header
							title={t('Permission Settings')}
							icon={<ProjectAccessSettingIcon style={{ width: '25px' }} />}
						/>

						<PermissionSettingTable
							control={control}
							permissionSettingItems={permissionSettingItems}
						/>
					</Box>

					{hasPermission(authState?.userInfo, [PermissionCodeEnum.RoleAssignUser]) && (
						<AddingUsersTable />
					)}

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
						<Button variant="contained" color="defaultButton" type="submit" disabled={isLoading}>
							{t('Confirm')}
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={() => navigate(-1)}
							disabled={isLoading}
						>
							{t('Cancel')}
						</Button>
					</Box>
				</FormProvider>
				<ValidationToast control={control} />
			</Box>
		</>
	)
}
